/**
 * Assistente de auto-cadastro em 7 etapas. Fonte: docs/00G §1.
 * Etapa 1 cria a conta (nome, e-mail, senha) e já autentica como cliente;
 * as etapas 2 a 7 preenchem o briefing, agrupado a partir de BRIEFING_STEPS.
 */
import { useEffect, useState } from "react";
import type { ChangeEvent, FormEvent } from "react";
import { Link, useParams } from "react-router";
import { BRIEFING_SCHEMA, BRIEFING_STEPS } from "@unik/shared";
import type { SessionDTO } from "@unik/shared";
import { api, ApiClientError } from "../lib/api";
import { useSession } from "../lib/session";
import { BrandMark } from "../ui/BrandMark";
import { Button } from "../ui/Button";
import { Input, TextArea } from "../ui/Input";
import { Select } from "../ui/Select";
import { FullScreenLoader } from "../ui/ProtectedRoute";
import { FloatingWhatsApp, WhatsAppIcon, UNIK_WHATSAPP_PHONE } from "../ui/FloatingWhatsApp";

type Phase = "checking" | "invalid" | "wizard" | "done";

const TOTAL_STEPS = 1 + BRIEFING_STEPS.length; // conta + 6 blocos de briefing = 7

export function OnboardingWizard() {
  const { token } = useParams();
  const { setSession } = useSession();

  const [phase, setPhase] = useState<Phase>("checking");
  const [invalidMessage, setInvalidMessage] = useState("Este link não é válido ou já foi usado.");
  const [step, setStep] = useState(0); // 0 = conta; 1..N = BRIEFING_STEPS[0..N-1]

  const [account, setAccount] = useState({ name: "", email: "", password: "", confirmPassword: "" });
  const [accountErrors, setAccountErrors] = useState<Record<string, string>>({});
  const [responses, setResponses] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get(`/onboarding/${token}`)
      .then(() => setPhase("wizard"))
      .catch((err) => {
        if (err instanceof ApiClientError) setInvalidMessage(err.message);
        setPhase("invalid");
      });
  }, [token]);

  function setField(key: string) {
    return (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      setResponses((r) => ({ ...r, [key]: e.target.value }));
    };
  }

  async function criarConta(e: FormEvent) {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (account.name.trim() === "") errs.name = "Informe seu nome.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(account.email)) errs.email = "Informe um e-mail válido.";
    if (account.password.length < 8) errs.password = "Pelo menos 8 caracteres.";
    if (account.confirmPassword !== account.password) errs.confirmPassword = "As senhas não coincidem.";
    if (Object.keys(errs).length > 0) {
      setAccountErrors(errs);
      return;
    }
    setAccountErrors({});
    setError(null);
    setSubmitting(true);
    try {
      const s = await api.post<SessionDTO>(`/onboarding/${token}/register`, {
        name: account.name.trim(), email: account.email.trim(), password: account.password,
      });
      setSession(s);
      setStep(1);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message);
        if (err.fields) setAccountErrors(err.fields);
      } else {
        setError("Não foi possível criar sua conta. Tente de novo.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function avancar() {
    setSubmitting(true);
    setError(null);
    try {
      await api.put("/approval/briefing", { responses, submit: false });
      setStep((s) => s + 1);
    } catch {
      setError("Não foi possível salvar. Verifique sua conexão e tente de novo.");
    } finally {
      setSubmitting(false);
    }
  }

  async function enviar() {
    setSubmitting(true);
    setError(null);
    try {
      await api.put("/approval/briefing", { responses, submit: true });
      setPhase("done");
    } catch {
      setError("Não foi possível enviar. Verifique sua conexão e tente de novo.");
    } finally {
      setSubmitting(false);
    }
  }

  if (phase === "checking") return <FullScreenLoader />;

  if (phase === "invalid") {
    return (
      <div className="app-shell flex min-h-dvh flex-col items-center justify-center px-6 text-center">
        <div className="w-full max-w-sm">
          <BrandMark variant="vinho" className="mx-auto h-6" />
          <h1 className="type-h2 mt-4 text-text-cream">Link indisponível</h1>
          <p className="type-body mt-3 text-text-secondary">{invalidMessage}</p>
          <p className="type-caption mt-6 text-text-tertiary">
            Já tem conta? <Link to="/entrar-cliente" className="text-gold">Entre por aqui</Link>. Senão,
            peça um novo link pra quem cuida do seu Instagram.
          </p>
        </div>
      </div>
    );
  }

  if (phase === "done") {
    return (
      <div className="app-shell flex min-h-dvh flex-col items-center justify-center px-6 text-center">
        <div className="w-full max-w-sm">
          <BrandMark variant="vinho" className="mx-auto h-6" />
          <h1 className="type-h2 mt-4 text-text-cream">Recebido, {account.name.split(" ")[0]}! 💛</h1>
          <p className="type-body mt-3 text-text-secondary">
            Obrigada por preencher tudo com tanto cuidado. Já estamos usando essas respostas no seu planejamento.
          </p>
          <div className="mt-8 flex flex-col gap-3">
            <a
              href={`https://wa.me/${UNIK_WHATSAPP_PHONE}?text=${encodeURIComponent(
                `Olá Lara! Acabei de concluir o onboarding e o briefing da Unik (${account.name || "Cliente"}).`
              )}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] px-5 py-3.5 text-sm font-semibold text-white shadow-[0_8px_20px_rgba(37,211,102,0.35)] hover:bg-[#20ba59] hover:scale-[1.02] active:scale-95 transition-all"
            >
              <WhatsAppIcon className="h-5 w-5 text-white shrink-0" />
              <span>Enviar no WhatsApp da Lara</span>
            </a>
            <Link to="/aprovacoes">
              <Button size="lg" shape="invite" variant="secondary" fullWidth>Ir para minha área</Button>
            </Link>
          </div>
        </div>
        <FloatingWhatsApp phone={UNIK_WHATSAPP_PHONE} message={`Olá Lara! Acabei de concluir o onboarding e o briefing da Unik (${account.name || "Cliente"}).`} label="Avisar no WhatsApp" />
      </div>
    );
  }

  const stepGroup = step >= 1 ? BRIEFING_STEPS[step - 1] : undefined;
  const sections = stepGroup
    ? BRIEFING_SCHEMA.filter((s) => stepGroup.sectionKeys.includes(s.key))
    : [];
  const isLast = step === BRIEFING_STEPS.length; // última etapa do briefing

  return (
    <div className="app-shell min-h-dvh px-5 py-6">
      <div className="mx-auto w-full max-w-lg">
        <BrandMark variant="vinho" className="h-6" />
        <div className="mt-3 flex items-center justify-between">
          <h1 className="type-h2 text-text-cream">
            {step === 0 ? "Vamos começar" : stepGroup?.title}
          </h1>
          <span className="type-caption text-text-tertiary">Etapa {step + 1} de {TOTAL_STEPS}</span>
        </div>
        <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-[var(--border-subtle)]">
          <div
            className="h-full rounded-full bg-[var(--accent)] transition-all"
            style={{ width: `${((step + 1) / TOTAL_STEPS) * 100}%` }}
          />
        </div>

        {step === 1 && (
          <p className="type-body mt-4 text-text-secondary">
            Olá, {account.name.split(" ")[0] || "tudo bem"}! Agora conta um pouco sobre sua empresa.
          </p>
        )}

        {step === 0 ? (
          <form onSubmit={criarConta} className="mt-6 flex flex-col gap-4" noValidate>
            <p className="type-body-sm text-text-secondary">
              Primeiro, crie seu acesso. É com ele que você vai entrar aqui depois.
            </p>
            <Input
              label="Seu nome completo" value={account.name} error={accountErrors.name}
              onChange={(e) => setAccount((a) => ({ ...a, name: e.target.value }))}
            />
            <Input
              label="E-mail" type="email" autoComplete="email" value={account.email} error={accountErrors.email}
              onChange={(e) => setAccount((a) => ({ ...a, email: e.target.value }))}
            />
            <Input
              label="Crie uma senha" type="password" autoComplete="new-password"
              value={account.password} error={accountErrors.password}
              onChange={(e) => setAccount((a) => ({ ...a, password: e.target.value }))}
              hint="Pelo menos 8 caracteres."
            />
            <Input
              label="Confirme a senha" type="password" autoComplete="new-password"
              value={account.confirmPassword} error={accountErrors.confirmPassword}
              onChange={(e) => setAccount((a) => ({ ...a, confirmPassword: e.target.value }))}
            />
            {error !== null && <p role="alert" className="type-caption text-danger">{error}</p>}
            <Button size="lg" shape="invite" type="submit" loading={submitting}>
              Criar minha conta
            </Button>
          </form>
        ) : (
          <div className="mt-6">
            <div className="flex flex-col gap-8">
              {sections.map((section) => (
                <section key={section.key}>
                  <h2 className="type-h3 text-text-cream">{section.title}</h2>
                  <p className="type-caption mt-1 text-text-tertiary">{section.description}</p>
                  <div className="mt-4 flex flex-col gap-4">
                    {section.fields.map((f) => {
                      if (f.type === "textarea") {
                        return (
                          <TextArea
                            key={f.key} label={f.label} value={responses[f.key] ?? ""}
                            onChange={setField(f.key)} placeholder={f.placeholder}
                          />
                        );
                      }
                      if (f.type === "select") {
                        return (
                          <Select key={f.key} label={f.label} value={responses[f.key] ?? ""} onChange={setField(f.key)}>
                            <option value="">Selecione…</option>
                            {(f.options ?? []).map((opt) => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </Select>
                        );
                      }
                      return (
                        <Input
                          key={f.key} label={f.label} value={responses[f.key] ?? ""}
                          onChange={setField(f.key)} placeholder={f.placeholder}
                        />
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>

            {error !== null && <p role="alert" className="type-caption mt-4 text-danger">{error}</p>}

            <div className="mt-8 flex gap-3">
              {/* A Etapa 2 não pode voltar pra criação de conta — o convite já foi consumido
                  e recriar a conta falharia. "Voltar" só existe a partir da Etapa 3. */}
              {step > 1 && (
                <Button variant="ghost" onClick={() => setStep((s) => s - 1)} disabled={submitting}>
                  Voltar
                </Button>
              )}
              {isLast ? (
                <Button size="lg" shape="invite" onClick={enviar} loading={submitting}>
                  Enviar diagnóstico
                </Button>
              ) : (
                <Button size="lg" shape="invite" onClick={avancar} loading={submitting}>
                  Avançar
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
